// PWA utility functions
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Network Information API types (not yet standardized)
export interface NetworkInformation {
  readonly downlink?: number;
  readonly effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
  readonly rtt?: number;
  readonly saveData?: boolean;
  readonly type?: 'bluetooth' | 'cellular' | 'ethernet' | 'none' | 'wifi' | 'wimax' | 'other' | 'unknown';
  onchange?: ((this: NetworkInformation, ev: Event) => void) | null;
}

// Extended Navigator interface for network information
interface NavigatorWithNetworkInfo extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

// Background Sync API types
interface SyncManager {
  register(tag: string): Promise<void>;
  getTags(): Promise<string[]>;
}

interface ServiceWorkerRegistrationWithSync extends ServiceWorkerRegistration {
  sync?: SyncManager;
}

export class PWAUtils {
  private static deferredPrompt: BeforeInstallPromptEvent | null = null;
  private static isInstalled = false;

  static init() {
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the mini-infobar from appearing
      e.preventDefault();
      // Save the event so it can be triggered later
      this.deferredPrompt = e as BeforeInstallPromptEvent;
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.deferredPrompt = null;
    });

    // Check if app is already installed
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      this.isInstalled = true;
    }

    // Register push notification event listeners
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage);
    }
  }

  static canInstall(): boolean {
    return this.deferredPrompt !== null && !this.isInstalled;
  }

  static isAppInstalled(): boolean {
    return this.isInstalled;
  }

  static async showInstallPrompt(): Promise<boolean> {
    if (!this.deferredPrompt) {
      console.warn('PWA: Install prompt not available');
      return false;
    }

    try {
      // Show the install prompt
      await this.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const choiceResult = await this.deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        this.deferredPrompt = null;
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('PWA: Error showing install prompt:', error);
      return false;
    }
  }

  static async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('PWA: This browser does not support notifications');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }

  static async subscribeToPushNotifications(): Promise<PushSubscription | null> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('PWA: Push messaging is not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;

      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        return existingSubscription;
      }

      // Request notification permission
      const permission = await this.requestNotificationPermission();
      if (permission !== 'granted') {
        console.warn('PWA: Notification permission denied');
        return null;
      }

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(
          // This would be your VAPID public key in production
          'BEl62iUYgUivxIkv69yViEuiBIa40HI80NmELGMqcRuebcdLQOBdlIJ2_Z_lZf6MZdmBDSaFhj-R0zJ_oYUawcI'
        )
      });

      return subscription;
    } catch (error) {
      console.error('PWA: Error subscribing to push notifications:', error);
      return null;
    }
  }

  static async unsubscribeFromPushNotifications(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const result = await subscription.unsubscribe();
        return result;
      }

      return true;
    } catch (error) {
      console.error('PWA: Error unsubscribing from push notifications:', error);
      return false;
    }
  }

  static async scheduleBackgroundSync(tag: string): Promise<void> {
    if (!('serviceWorker' in navigator) || !('sync' in window.ServiceWorkerRegistration.prototype)) {
      console.warn('PWA: Background sync is not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready as ServiceWorkerRegistrationWithSync;
      if (registration.sync) {
        await registration.sync.register(tag);
      }
    } catch (error) {
      console.error('PWA: Error scheduling background sync:', error);
    }
  }

  static showNotification(title: string, options?: NotificationOptions): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        icon: '/logo192.png',
        badge: '/favicon.ico',
        ...options
      });
    }
  }

  static async getNetworkStatus(): Promise<{online: boolean; connection?: NetworkInformation | null}> {
    const online = navigator.onLine;
    let connection: NetworkInformation | null = null;

    const nav = navigator as NavigatorWithNetworkInfo;
    if (nav.connection) {
      connection = nav.connection;
    } else if (nav.mozConnection) {
      connection = nav.mozConnection;
    } else if (nav.webkitConnection) {
      connection = nav.webkitConnection;
    }

    return { online, connection };
  }

  private static handleServiceWorkerMessage = (event: MessageEvent) => {
    const { type, payload } = event.data;

    switch (type) {
      case 'BACKGROUND_SYNC_COMPLETE':
        // Dispatch custom event for components to listen to
        window.dispatchEvent(new CustomEvent('pwa:sync-complete', { detail: payload }));
        break;
      case 'BACKGROUND_SYNC_FAILED':
        console.error('PWA: Background sync failed:', payload);
        window.dispatchEvent(new CustomEvent('pwa:sync-failed', { detail: payload }));
        break;
      case 'GET_AUTH_TOKEN':
        // Service worker is requesting auth token for background sync
        PWAUtils.handleAuthTokenRequest(event);
        break;
      default:
        // Other message types (PUSH_NOTIFICATION, BACKGROUND_SYNC, CACHE_UPDATE) need no handling
        break;
    }
  };

  /**
   * Handle auth token request from service worker
   */
  private static handleAuthTokenRequest(event: MessageEvent): void {
    try {
      const authData = localStorage.getItem('auth');
      let token = null;

      if (authData) {
        const parsed = JSON.parse(authData);
        token = parsed.accessToken || null;
      }

      // Send token back through the message port
      if (event.ports && event.ports[0]) {
        event.ports[0].postMessage({ token });
      }
    } catch (error) {
      console.error('PWA: Error handling auth token request:', error);
      if (event.ports && event.ports[0]) {
        event.ports[0].postMessage({ error: 'Failed to get auth token' });
      }
    }
  }

  private static urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }
}

// Initialize PWA utilities when module is loaded
if (typeof window !== 'undefined') {
  PWAUtils.init();
}